import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

// SHA-256 hashing using Web Crypto API
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if password is required from environment
  const requirePassword = import.meta.env.VITE_REQUIRE_PASSWORD === 'true';
  const passwordHash = import.meta.env.VITE_APP_PASSWORD_HASH || '';

  // Check sessionStorage on mount
  useEffect(() => {
    if (!requirePassword) {
      // Password not required - auto-authenticate
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const authToken = sessionStorage.getItem('ai-grading-auth');
    if (authToken === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, [requirePassword]);

  // Login function with hash comparison
  const login = useCallback(async (password) => {
    if (!requirePassword) {
      setIsAuthenticated(true);
      return true;
    }

    try {
      const hash = await sha256(password);

      if (hash === passwordHash) {
        sessionStorage.setItem('ai-grading-auth', 'authenticated');
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, [requirePassword, passwordHash]);

  // Logout function
  const logout = useCallback(() => {
    sessionStorage.removeItem('ai-grading-auth');
    setIsAuthenticated(false);
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    requirePassword,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
